import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { TestPanel, TestDefinition, TestType, LabTestDefinition } from '../../types/procedures';
import { ProcedureService } from '../../services/procedureService';

// Type guard function
const isLabTestDefinition = (def: TestDefinition): def is LabTestDefinition => {
  return def.testType === TestType.LAB && 'analytes' in def;
};

// Styled Components - Modern Theme
const HeaderCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  marginBottom: theme.spacing(3),
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
}));

const StyledTableContainer = styled(Paper)(({ theme }) => ({
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  overflow: 'hidden',
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' 
    ? theme.palette.grey[800] 
    : theme.palette.grey[100],
  '& .MuiTableCell-head': {
    fontWeight: 600,
    fontSize: '0.875rem',
    color: theme.palette.text.primary,
    borderBottom: `2px solid ${theme.palette.divider}`,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const ModernChip = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.palette.primary.light,
  color: theme.palette.primary.contrastText,
  fontWeight: 500,
  margin: theme.spacing(0.25),
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
  },
}));

const procedureService = new ProcedureService();

export const TestPanelManager: React.FC = () => {
  const theme = useTheme();
  const [panels, setPanels] = useState<TestPanel[]>([]);
  const [testDefinitions, setTestDefinitions] = useState<TestDefinition[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPanel, setEditingPanel] = useState<TestPanel | null>(null);
  const [panelName, setPanelName] = useState('');
  const [panelDescription, setPanelDescription] = useState('');
  const [selectedTests, setSelectedTests] = useState<TestDefinition[]>([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const [panelsData, definitionsData] = await Promise.all([
      procedureService.getTestPanels(),
      procedureService.getTestDefinitions(),
    ]);

    setPanels(panelsData);
    setTestDefinitions(definitionsData);
  };

  const handleCreatePanel = async () => {
    if (!panelName || selectedTests.length === 0) return;

    if (editingPanel) {
      // Update existing panel
      const updatedPanel = await procedureService.updateTestPanel(editingPanel.id, {
        name: panelName,
        description: panelDescription,
        tests: selectedTests,
      });

      setPanels(panels.map(panel => 
        panel.id === editingPanel.id ? updatedPanel : panel
      ));
    } else {
      // Create new panel
      const newPanel = await procedureService.createTestPanel({
        name: panelName,
        description: panelDescription,
        tests: selectedTests,
      });

      setPanels([...panels, newPanel]);
    }

    setOpenDialog(false);
    resetForm();
  };

  const handleEditPanel = (panel: TestPanel) => {
    setEditingPanel(panel);
    setPanelName(panel.name);
    setPanelDescription(panel.description || '');
    
    // Handle both analytes and tests arrays
    if ((panel as any).analytes && Array.isArray((panel as any).analytes)) {
      // For panels with analytes, try to find matching test definitions
      const analyteNames = (panel as any).analytes as string[];
      const matchingTestDefinitions = testDefinitions.filter(def => {
        // Type guard to check if this is a LabTestDefinition with analytes
        return isLabTestDefinition(def) && 
               def.analytes && 
               def.analytes.some(analyte => analyteNames.includes(analyte));
      });
      setSelectedTests(matchingTestDefinitions);
    } else {
      // Convert panel.tests to TestDefinition objects for selectedTests
      const panelTestIds = panel.tests?.map(test => 
        typeof test === 'string' ? test : test.id
      ) || [];
      
      const panelTestDefinitions = testDefinitions.filter(def => 
        panelTestIds.includes(def.id)
      );
      
      setSelectedTests(panelTestDefinitions);
    }
    
    setOpenDialog(true);
  };

  const handleNewPanel = () => {
    setEditingPanel(null);
    resetForm();
    setOpenDialog(true);
  };

  const resetForm = () => {
    setPanelName('');
    setPanelDescription('');
    setSelectedTests([]);
    setEditingPanel(null);
  };

  const handleAddTest = (test: TestDefinition) => {
    if (!selectedTests.find(t => t.id === test.id)) {
      setSelectedTests([...selectedTests, test]);
    }
  };

  const handleRemoveTest = (testId: string) => {
    setSelectedTests(selectedTests.filter(test => test.id !== testId));
  };

  return (
    <Box sx={{ p: 4, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', minHeight: '100vh' }}>
      <HeaderCard>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                Test Panels Management
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                Organize test definitions into comprehensive panels for efficient ordering
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleNewPanel}
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.3)',
                },
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1.5,
              }}
            >
              New Panel
            </Button>
          </Box>
        </CardContent>
      </HeaderCard>

      <StyledTableContainer>
        <TableContainer>
          <Table sx={{ minWidth: 650 }} aria-label="test panels table">
          <StyledTableHead>
            <TableRow>
              <TableCell sx={{ width: '20%' }}>Panel Name</TableCell>
              <TableCell sx={{ width: '25%' }}>Description</TableCell>
              <TableCell sx={{ width: '45%' }}>Tests / Analytes</TableCell>
              <TableCell sx={{ width: '10%' }} align="right">Actions</TableCell>
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {panels.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <Box textAlign="center" py={6}>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No test panels created yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Create your first test panel to organize related tests together
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              panels.map((panel) => (
                <StyledTableRow key={panel.id}>
                  <TableCell>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {panel.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {panel.description || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {/* Display analytes if available, otherwise display tests */}
                      {(panel as any).analytes && Array.isArray((panel as any).analytes) ? (
                        (panel as any).analytes.map((analyte: string, index: number) => (
                          <ModernChip
                            key={`analyte-${index}`}
                            label={analyte}
                            size="small"
                          />
                        ))
                      ) : (
                        panel.tests && Array.isArray(panel.tests) && panel.tests.map((test, index) => {
                          // Handle both cases: test as object or test as string ID
                          const testId = typeof test === 'string' ? test : test.id;
                          const testName = typeof test === 'string' 
                            ? testDefinitions.find(def => def.id === test)?.name || test
                            : test.name;
                          
                          return (
                            <ModernChip
                              key={testId || `test-${index}`}
                              label={testName}
                              size="small"
                            />
                          );
                        })
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit Panel">
                      <IconButton
                        onClick={() => handleEditPanel(panel)}
                        sx={{
                          color: theme.palette.primary.main,
                          '&:hover': {
                            backgroundColor: theme.palette.primary.light + '20',
                          },
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </StyledTableRow>
              ))
            )}
          </TableBody>
        </Table>
        </TableContainer>
      </StyledTableContainer>

      <Dialog 
        open={openDialog} 
        onClose={() => { setOpenDialog(false); resetForm(); }} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 24px 48px rgba(0, 0, 0, 0.15)',
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {editingPanel ? 'Edit Test Panel' : 'Create New Test Panel'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Panel Name"
              value={panelName}
              onChange={(e) => setPanelName(e.target.value)}
              sx={{ mb: 3 }}
            />
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Description"
              value={panelDescription}
              onChange={(e) => setPanelDescription(e.target.value)}
              sx={{ mb: 3 }}
            />
            
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Available Tests
            </Typography>
            <Box sx={{ maxHeight: 200, overflow: 'auto', mb: 3 }}>
              <List>
                {testDefinitions.map((test) => (
                  <ListItem 
                    key={test.id}
                    sx={{
                      borderRadius: '8px',
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                      }
                    }}
                  >
                    <ListItemText
                      primary={test.name}
                      secondary={`${test.codeValue} - ${test.testType}`}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={() => handleAddTest(test)}
                        disabled={selectedTests.some(t => t.id === test.id)}
                        sx={{
                          backgroundColor: selectedTests.some(t => t.id === test.id) 
                            ? theme.palette.action.disabledBackground 
                            : theme.palette.primary.light,
                          color: 'white',
                          '&:hover': {
                            backgroundColor: theme.palette.primary.main,
                          },
                          '&:disabled': {
                            color: theme.palette.action.disabled,
                          }
                        }}
                      >
                        <AddIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Box>
            
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Selected Tests ({selectedTests.length})
            </Typography>
            <Box sx={{ maxHeight: 150, overflow: 'auto' }}>
              <List>
                {selectedTests.map((test) => (
                  <ListItem 
                    key={test.id}
                    sx={{
                      borderRadius: '8px',
                      backgroundColor: theme.palette.primary.light + '20',
                      mb: 1,
                    }}
                  >
                    <ListItemText
                      primary={test.name}
                      secondary={`${test.codeValue} - ${test.testType}`}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={() => handleRemoveTest(test.id)}
                        sx={{
                          backgroundColor: theme.palette.error.light,
                          color: 'white',
                          '&:hover': {
                            backgroundColor: theme.palette.error.main,
                          }
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button 
            onClick={() => { setOpenDialog(false); resetForm(); }}
            sx={{ textTransform: 'none', fontWeight: 500 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreatePanel}
            variant="contained"
            disabled={!panelName || selectedTests.length === 0}
            sx={{
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 600,
              px: 3
            }}
          >
            {editingPanel ? 'Update Panel' : 'Create Panel'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}; 